/*
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useMemo } from "react";
import { ThemeEditor } from "../../ThemeEditor";
import { useAbility, useResourceInfo, useStateWithEvent, useViewInfo } from "../../../../../ui/hooks";
import { ToolbarCommandButton } from "../../../../../ui/components/Toolbar/ToolbarCommandButton";
import { CMD } from "../../../../../commands/CMD";
import { ToolbarSpacer } from "../../../../../ui/components/Toolbar/ToolbarSpacer";
import { ToolbarButton } from "../../../../../ui/components/Toolbar/ToolbarButton";
import { useLayoutElement } from "../../../../../ui/components/Layout/useLayoutElement";
import { LayoutState_Column } from "../../../../../ui/components/Layout/LayoutState";
import { ABILITY_ACTIONS } from "../../../../../ability/AbilityService";
import { FC_ANCHOR } from "../../../../../ui/components/FloatingContainer";
import { DROPDOWN_STYLE } from "../../../../../ui/components/DropdownMenu";
import { useDeepCompareMemo } from "use-deep-compare";

export interface IThemeEditorMainToolbarProps {
	view: ThemeEditor;
}

export const ThemeEditorMainToolbar: React.FC<IThemeEditorMainToolbarProps> = ({ view }) => {
	const themeModel = useMemo(() => view.getThemeModel(), [ view ]);
	const layout = view.getLayout();
	const viewInfo = useViewInfo(view);
	const resourceInfo = useResourceInfo(view);
	const ability = useAbility();
	const saveAllowed = useDeepCompareMemo(() => view.canSave(), [ viewInfo, resourceInfo ]);

	const enabled = useStateWithEvent(themeModel.onModelChange, () => ({
		undo: view.canUndo(),
		redo: view.canRedo()
	}));

	const panelLeft = useLayoutElement<LayoutState_Column>(layout.getById("panelLeft"));
	const panelRight = useLayoutElement<LayoutState_Column>(layout.getById("panelRight"));

	return (
		<>
			<ToolbarButton
				icon="mdi/dock-left"
				active={panelLeft.isExpanded}
				onClick={panelLeft.toggleExpanded}
			/>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/undo-variant"
				disabled={!enabled.undo}
				commandId={CMD.VIEW_EDITOR.EDIT.UNDO}
			/>

			<ToolbarCommandButton
				icon="mdi/redo-variant"
				disabled={!enabled.redo}
				commandId={CMD.VIEW_EDITOR.EDIT.REDO}
			/>

			<ToolbarSpacer grow={1} />

			<ToolbarCommandButton
				icon="mdi/content-save"
				commandId="themeEditor.edit.saveResource"
				disabled={!saveAllowed}
				tooltip="Save"
			/>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/menu"
				tooltip="More options"
				menuItems={[
					{
						id: "rename",
						label: "Rename",
						icon: "mdi/pencil",
						commandId: "resource.rename",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: !ability.can(ABILITY_ACTIONS.RENAME, resourceInfo.resource)
					},
					{
						id: "move",
						label: "Move",
						icon: "mdi/folder-move",
						commandId: "resource.moveDialog.open",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: !ability.can(ABILITY_ACTIONS.RENAME, resourceInfo.resource)
					},
					{
						id: "duplicate",
						label: "Duplicate",
						icon: "mdi/content-copy",
						commandId: "resource.duplicate",
						commandArgs: [ resourceInfo.resource?.id, true ]
					},
					{
						id: "history",
						label: "Revision History",
						icon: "mdi/history",
						commandId: "resource.openRevisionHistory",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: true
					},
					{
						id: "export",
						label: "Export to YAML",
						icon: "mdi/export",
						commandId: "resource.export",
						commandArgs: [ resourceInfo.resource?.id ]
					},
					{
						id: "delete",
						label: "Delete",
						icon: "mdi/delete",
						commandId: "resource.delete",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: !ability.can(ABILITY_ACTIONS.REMOVE, resourceInfo.resource)
					}
				]}
				menuAnchor={FC_ANCHOR.RIGHT_BOTTOM}
				menuStyle={DROPDOWN_STYLE.COMPACT}
			/>

			<ToolbarSpacer />

			<ToolbarButton
				icon="mdi/dock-right"
				active={panelRight.isExpanded}
				onClick={panelRight.toggleExpanded}
			/>
		</>
	);
};
